import type { CSSProperties } from 'react';
import { SUPPORTED_LANGUAGE_CODES, type SupportedLanguage } from '@/i18n/registry';

export type PlatformLanguage = SupportedLanguage;
export type PlatformFontFamily = 'Plus Jakarta Sans' | 'Inter' | 'Poppins' | 'Roboto';

export interface PlatformNavLink {
  id: string;
  label: string;
  href: string;
  children?: PlatformNavLink[];
}

export interface PlatformFooterSection {
  id: string;
  title: string;
  links: PlatformNavLink[];
}

export interface PlatformBrandingSettings {
  appName: string;
  shortBrandName: string;
  tagline: string;
  logoUrl: string;
  compactLogoUrl: string;
  faviconUrl: string;
  appleTouchIconUrl: string;
  pwaIcon192Url: string;
  pwaIcon512Url: string;
  socialImageUrl: string;
  emailLogoUrl: string;
  organizationLogoUrl: string;
  primaryColor: string;
  accentColor: string;
  fontFamily: PlatformFontFamily;
}

export interface PlatformSeoSettings {
  siteTitle: string;
  titleTemplate: string;
  siteDescription: string;
  keywords: string[];
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterTitle: string;
  twitterDescription: string;
  twitterImage: string;
  twitterHandle: string;
  twitterCard: 'summary' | 'summary_large_image';
  robotsIndex: boolean;
  robotsFollow: boolean;
  sitemapEnabled: boolean;
  googleSiteVerification: string;
  bingSiteVerification: string;
  organizationName: string;
  organizationLegalName: string;
  organizationDescription: string;
  home: {
    title: string;
    description: string;
    keywords: string[];
    ogTitle: string;
    ogDescription: string;
    socialImage: string;
    twitterTitle: string;
    twitterDescription: string;
    twitterImage: string;
    robotsIndex: boolean;
    robotsFollow: boolean;
  };
}

export interface PlatformPublicSettings {
  headerMenu: PlatformNavLink[];
  stickyHeader: boolean;
  footerSections: PlatformFooterSection[];
  footerTagline: string;
  footerCompanyName: string;
  footerWebsiteUrl: string;
  footerCopyright: string;
  footerPoweredByText: string;
  footerPoweredByUrl: string;
  contactEmail: string;
  contactPhone: string;
  contactPhoneCountryCode: string;
  contactPhoneFormatted: string;
  contactAddress: string;
  socialTwitter: string;
  socialGithub: string;
  socialLinkedin: string;
}

export interface PlatformEmailSettings {
  provider: 'supabase' | 'smtp';
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  fromEmail: string;
  fromName: string;
  replyToEmail: string;
  supportEmail: string;
  emailLogoUrl: string;
  footerCompanyName: string;
  footerWebsiteUrl: string;
  footerCopyright: string;
  testRecipientEmail: string;
}

export interface PlatformAnalyticsSettings {
  googleAnalyticsId: string;
  googleTagManagerId: string;
}

export interface PlatformAuthSettings {
  emailPasswordEnabled: boolean;
  googleOauthEnabled: boolean;
  appleOauthEnabled: boolean;
  magicLinkEnabled: boolean;
  requireEmailVerification: boolean;
  passwordMinLength: number;
}

export interface PlatformLocalizationSettings {
  defaultLanguage: PlatformLanguage;
  enabledLanguages: PlatformLanguage[];
}

export interface PlatformSettingsSnapshot {
  branding: PlatformBrandingSettings;
  seo: PlatformSeoSettings;
  publicUi: PlatformPublicSettings;
  email: PlatformEmailSettings;
  analytics: PlatformAnalyticsSettings;
  auth: PlatformAuthSettings;
  localization: PlatformLocalizationSettings;
  updatedAt?: string;
  raw: Record<string, unknown>;
}

export const PRODUCTION_CANONICAL_ORIGIN = 'https://1smartpocket.com';
export const SMART_POCKET_DEFAULT_LOGO = '';
export const SMART_POCKET_DEFAULT_COMPACT_LOGO = SMART_POCKET_DEFAULT_LOGO;
export const SMART_POCKET_DEFAULT_SOCIAL_IMAGE = '/assets/images/smart-pocket-social-card.png';
export const SMART_POCKET_LEGACY_SOCIAL_IMAGE_SVG = '/assets/images/smart-pocket-social-card.svg';
export const SMART_POCKET_DEFAULT_ICON = '/favicon.ico';
export const SMART_POCKET_DEFAULT_FAVICON = '/favicon.ico';
export const SMART_POCKET_DEFAULT_PWA_ICON_192 = '/icons/smart-pocket-192.png';
export const SMART_POCKET_DEFAULT_PWA_ICON_512 = '/icons/smart-pocket-512.png';
export const SMART_POCKET_SAFE_FALLBACK_IMAGE = '/assets/images/no_image.png';
export const SMART_POCKET_LEGACY_WALLET_IMAGE = '/assets/images/app_logo.png';
export const SMART_POCKET_DEFAULT_SITE_TITLE = 'Smart Pocket — Personal Finance, Simplified';
export const SMART_POCKET_DEFAULT_SITE_DESCRIPTION =
  'Smart Pocket helps you track income, expenses, budgets, and financial accounts with professional reporting and a clean mobile-first interface.';
export const SMART_POCKET_DEFAULT_KEYWORDS = [
  'personal finance',
  'budgeting',
  'expense tracking',
  'money management',
];

const LANDING_SECTION_DESTINATIONS = {
  '/#about': '/home#about',
  '/#features': '/home#features',
  '/#pricing': '/home#pricing',
  '/about': '/home#about',
  '/features': '/home#features',
  '/pricing': '/home#pricing',
} as const;

function normalizePublicDestination(href: string) {
  const trimmed = href.trim();
  if (!trimmed) {
    return '';
  }

  const [pathPart, hashPart] = trimmed.split('#', 2);
  const normalizedPath = pathPart
    ? (pathPart.startsWith('/') ? pathPart : `/${pathPart}`)
        .replace(/\/{2,}/g, '/')
        .replace(/\/+$/g, '') || '/'
    : '/';
  const normalizedHash = hashPart ? `#${hashPart.trim().toLowerCase()}` : '';
  return `${normalizedPath}${normalizedHash}`;
}

export function mapMarketingHrefToHomeAnchor(href: string) {
  const normalized = normalizePublicDestination(href);
  if (!normalized) {
    return normalized;
  }

  return LANDING_SECTION_DESTINATIONS[
    normalized as keyof typeof LANDING_SECTION_DESTINATIONS
  ] || normalized;
}

export function normalizePublicNavHref(href: string) {
  return mapMarketingHrefToHomeAnchor(href);
}

export const DEFAULT_HEADER_MENU: PlatformNavLink[] = [
  { id: 'hm-about', label: 'About', href: '/home#about' },
  { id: 'hm-features', label: 'Features', href: '/home#features' },
  { id: 'hm-pricing', label: 'Pricing', href: '/home#pricing' },
  {
    id: 'hm-documentation',
    label: 'Documentation',
    href: '/help/documentation',
    children: [
      { id: 'hm-docs-main', label: 'All guides', href: '/help/documentation' },
    ],
  },
  { id: 'hm-contact', label: 'Contact', href: '/contact' },
];

export const DEFAULT_FOOTER_SECTIONS: PlatformFooterSection[] = [
  {
    id: 'footer-section-product',
    title: 'Product',
    links: [
      { id: 'fl-features', label: 'Features', href: '/home#features' },
      { id: 'fl-pricing', label: 'Pricing', href: '/home#pricing' },
      { id: 'fl-desktop-app', label: 'Desktop App', href: '/desktop-app' },
    ],
  },
  {
    id: 'footer-section-learn',
    title: 'Learn',
    links: [
      { id: 'fl-blog', label: 'Blog', href: '/blog' },
      { id: 'fl-documentation', label: 'Documentation', href: '/help/documentation' },
      { id: 'fl-ai-receipt-scanner', label: 'AI Receipt Scanner', href: '/ai-receipt-scanner' },
      { id: 'fl-ai-voice-expense-tracker', label: 'AI Voice Expense Tracker', href: '/ai-voice-expense-tracker' },
    ],
  },
  {
    id: 'footer-section-company',
    title: 'Company',
    links: [
      { id: 'fl-about', label: 'About', href: '/home#about' },
      { id: 'fl-contact', label: 'Contact Us', href: '/contact' },
      { id: 'fl-faqs', label: 'FAQs', href: '/faqs' },
    ],
  },
  {
    id: 'footer-section-legal',
    title: 'Legal & Trust',
    links: [
      { id: 'fl-security', label: 'Security', href: '/security' },
      { id: 'fl-privacy', label: 'Privacy Policy', href: '/privacy' },
      { id: 'fl-terms', label: 'Terms and Conditions', href: '/terms' },
    ],
  },
];

const LEGACY_FOOTER_SECTION_TITLE_TO_ID = {
  company: 'fs-company',
  product: 'fs-product',
  support: 'fs-support',
  legal: 'fs-legal',
} as const;

const DEFAULT_PUBLIC_CONTACT_ADDRESS = 'Al Majaz 2, Sharjah - UAE';

function normalizeLegacyPublicLabel(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

export const DEFAULT_PLATFORM_SETTINGS: PlatformSettingsSnapshot = {
  branding: {
    appName: 'Smart Pocket',
    shortBrandName: 'Smart Pocket',
    tagline: 'Personal Finance, Simplified',
    logoUrl: SMART_POCKET_DEFAULT_LOGO,
    compactLogoUrl: SMART_POCKET_DEFAULT_COMPACT_LOGO,
    faviconUrl: SMART_POCKET_DEFAULT_FAVICON,
    appleTouchIconUrl: SMART_POCKET_DEFAULT_ICON,
    pwaIcon192Url: SMART_POCKET_DEFAULT_PWA_ICON_192,
    pwaIcon512Url: SMART_POCKET_DEFAULT_PWA_ICON_512,
    socialImageUrl: SMART_POCKET_DEFAULT_SOCIAL_IMAGE,
    emailLogoUrl: SMART_POCKET_DEFAULT_LOGO,
    organizationLogoUrl: SMART_POCKET_DEFAULT_LOGO,
    primaryColor: '#0f3460',
    accentColor: '#00b4d8',
    fontFamily: 'Plus Jakarta Sans',
  },
  seo: {
    siteTitle: SMART_POCKET_DEFAULT_SITE_TITLE,
    titleTemplate: '%s | Smart Pocket',
    siteDescription: SMART_POCKET_DEFAULT_SITE_DESCRIPTION,
    keywords: SMART_POCKET_DEFAULT_KEYWORDS,
    canonicalUrl: PRODUCTION_CANONICAL_ORIGIN,
    ogTitle: SMART_POCKET_DEFAULT_SITE_TITLE,
    ogDescription: SMART_POCKET_DEFAULT_SITE_DESCRIPTION,
    ogImage: SMART_POCKET_DEFAULT_SOCIAL_IMAGE,
    twitterTitle: SMART_POCKET_DEFAULT_SITE_TITLE,
    twitterDescription: SMART_POCKET_DEFAULT_SITE_DESCRIPTION,
    twitterImage: SMART_POCKET_DEFAULT_SOCIAL_IMAGE,
    twitterHandle: '@smartpocket',
    twitterCard: 'summary_large_image',
    robotsIndex: true,
    robotsFollow: true,
    sitemapEnabled: true,
    googleSiteVerification: '',
    bingSiteVerification: '',
    organizationName: 'Smart Pocket',
    organizationLegalName: '',
    organizationDescription: SMART_POCKET_DEFAULT_SITE_DESCRIPTION,
    home: {
      title: SMART_POCKET_DEFAULT_SITE_TITLE,
      description: SMART_POCKET_DEFAULT_SITE_DESCRIPTION,
      keywords: [],
      ogTitle: SMART_POCKET_DEFAULT_SITE_TITLE,
      ogDescription: SMART_POCKET_DEFAULT_SITE_DESCRIPTION,
      socialImage: SMART_POCKET_DEFAULT_SOCIAL_IMAGE,
      twitterTitle: SMART_POCKET_DEFAULT_SITE_TITLE,
      twitterDescription: SMART_POCKET_DEFAULT_SITE_DESCRIPTION,
      twitterImage: SMART_POCKET_DEFAULT_SOCIAL_IMAGE,
      robotsIndex: true,
      robotsFollow: true,
    },
  },
  publicUi: {
    headerMenu: DEFAULT_HEADER_MENU,
    stickyHeader: true,
    footerSections: DEFAULT_FOOTER_SECTIONS,
    footerTagline: 'Personal finance, simplified.',
    footerCompanyName: 'Smart Pocket',
    footerWebsiteUrl: PRODUCTION_CANONICAL_ORIGIN,
    footerCopyright: '© Smart Pocket. All rights reserved.',
    footerPoweredByText: 'MCS Consultancy',
    footerPoweredByUrl: 'https://www.mc1services.com/',
    contactEmail: '',
    contactPhone: '',
    contactPhoneCountryCode: '',
    contactPhoneFormatted: '',
    contactAddress: '',
    socialTwitter: '',
    socialGithub: '',
    socialLinkedin: '',
  },
  email: {
    provider: 'supabase',
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    fromEmail: 'no-reply@1smartpocket.com',
    fromName: 'Smart Pocket',
    replyToEmail: 'info@1smartpocket.com',
    supportEmail: 'info@1smartpocket.com',
    emailLogoUrl: SMART_POCKET_DEFAULT_LOGO,
    footerCompanyName: 'Smart Pocket',
    footerWebsiteUrl: PRODUCTION_CANONICAL_ORIGIN,
    footerCopyright: '© Smart Pocket. All rights reserved.',
    testRecipientEmail: '',
  },
  analytics: {
    googleAnalyticsId: '',
    googleTagManagerId: '',
  },
  auth: {
    emailPasswordEnabled: true,
    googleOauthEnabled: false,
    appleOauthEnabled: false,
    magicLinkEnabled: false,
    requireEmailVerification: true,
    passwordMinLength: 8,
  },
  localization: {
    defaultLanguage: 'en',
    enabledLanguages: [...SUPPORTED_LANGUAGE_CODES],
  },
  updatedAt: undefined,
  raw: {},
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function sanitizeHexColor(value: unknown, fallback: string) {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return /^#([0-9a-fA-F]{6})$/.test(normalized) ? normalized : fallback;
}

function sanitizeFontFamily(value: unknown): PlatformBrandingSettings['fontFamily'] {
  switch (value) {
    case 'Inter':
    case 'Poppins':
    case 'Roboto':
      return value;
    default:
      return 'Plus Jakarta Sans';
  }
}

function sanitizeNonEmptyString(value: unknown, fallback: string) {
  return typeof value === 'string' && value.trim() ? value.trim() : fallback;
}

function sanitizeOptionalString(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeAssetIdentity(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  try {
    const parsed = new URL(trimmed, PRODUCTION_CANONICAL_ORIGIN);
    return `${parsed.origin}${parsed.pathname}`.toLowerCase();
  } catch {
    return trimmed.toLowerCase();
  }
}

export function isDisallowedSocialPreviewAsset(value: string, blockedSources: string[] = []) {
  const normalized = normalizeAssetIdentity(value);
  if (!normalized) {
    return false;
  }

  const reservedAssets = [
    SMART_POCKET_LEGACY_WALLET_IMAGE,
    SMART_POCKET_LEGACY_SOCIAL_IMAGE_SVG,
    SMART_POCKET_DEFAULT_COMPACT_LOGO,
    SMART_POCKET_DEFAULT_ICON,
    SMART_POCKET_DEFAULT_FAVICON,
    SMART_POCKET_SAFE_FALLBACK_IMAGE,
  ]
    .map(normalizeAssetIdentity)
    .filter(Boolean);

  const blocked = blockedSources
    .map(normalizeAssetIdentity)
    .filter(Boolean);

  return reservedAssets.includes(normalized) || blocked.includes(normalized);
}

export function getApprovedSocialPreviewAsset(value: string, blockedSources: string[] = []) {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  return isDisallowedSocialPreviewAsset(trimmed, blockedSources) ? '' : trimmed;
}

function sanitizeBoolean(value: unknown, fallback: boolean) {
  return typeof value === 'boolean' ? value : fallback;
}

function sanitizeSupportedLanguage(value: unknown): PlatformLocalizationSettings['defaultLanguage'] {
  switch (value) {
    case 'ar':
    case 'fr':
    case 'ru':
    case 'tr':
    case 'zh-CN':
    case 'es':
    case 'pt-BR':
      return value;
    case 'en':
    default:
      return 'en';
  }
}

function isPlatformLanguage(value: unknown): value is PlatformLanguage {
  return (
    value === 'en'
    || value === 'ar'
    || value === 'fr'
    || value === 'ru'
    || value === 'tr'
    || value === 'zh-CN'
    || value === 'es'
    || value === 'pt-BR'
  );
}

function sanitizeEnabledLanguageEntry(value: unknown): PlatformLanguage | null {
  return isPlatformLanguage(value) ? value : null;
}

function sanitizeTwitterCard(value: unknown): PlatformSeoSettings['twitterCard'] {
  return value === 'summary' ? 'summary' : 'summary_large_image';
}

function normalizeEnabledLanguages(value: unknown) {
  const fallback = DEFAULT_PLATFORM_SETTINGS.localization.enabledLanguages;
  if (!Array.isArray(value)) return fallback;

  const normalized: PlatformLanguage[] = [];
  const seen = new Set<PlatformLanguage>();
  for (const entry of value) {
    const platformLang = sanitizeEnabledLanguageEntry(entry);
    if (!platformLang || seen.has(platformLang)) continue;
    seen.add(platformLang);
    normalized.push(platformLang);
  }

  return normalized.length > 0 ? normalized : fallback;
}

export function normalizeSeoKeywordList(value: unknown, fallback: string[] = []) {
  const dedupe = (entries: string[]) => {
    const seen = new Set<string>();
    const normalized: string[] = [];

    for (const entry of entries) {
      if (seen.has(entry)) {
        continue;
      }

      seen.add(entry);
      normalized.push(entry);
    }

    return normalized;
  };

  if (Array.isArray(value)) {
    const normalized = dedupe(
      value
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter(Boolean)
    );
    return normalized.length > 0 ? normalized : fallback;
  }

  if (typeof value === 'string') {
    const normalized = dedupe(
      value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
    );
    return normalized.length > 0 ? normalized : fallback;
  }

  return fallback;
}

function normalizeNavLinks(value: unknown, fallback: PlatformNavLink[]) {
  if (!Array.isArray(value)) return fallback;
  const seenDestinations = new Set<string>();
  const walk = (entry: unknown, index: number): PlatformNavLink | null => {
    if (!isObject(entry)) return null;
    const id = typeof entry.id === 'string' && entry.id.trim() ? entry.id.trim() : `link-${index}`;
    const label = typeof entry.label === 'string' && entry.label.trim() ? entry.label.trim() : '';
    const href = typeof entry.href === 'string' && entry.href.trim() ? normalizePublicNavHref(entry.href.trim()) : '';
    if (!label || !href) return null;
    if (seenDestinations.has(href)) return null;
    seenDestinations.add(href);
    const normalized: PlatformNavLink = { id, label, href };
    if (Array.isArray(entry.children)) {
      const childSeen = new Set<string>();
      const children: PlatformNavLink[] = [];
      for (let i = 0; i < entry.children.length; i++) {
        const rawChild = entry.children[i];
        if (!isObject(rawChild)) continue;
        const childId =
          typeof rawChild.id === 'string' && rawChild.id.trim()
            ? rawChild.id.trim()
            : `${id}-child-${i}`;
        const childLabel =
          typeof rawChild.label === 'string' && rawChild.label.trim() ? rawChild.label.trim() : '';
        const childHref =
          typeof rawChild.href === 'string' && rawChild.href.trim()
            ? normalizePublicNavHref(rawChild.href.trim())
            : '';
        if (!childLabel || !childHref) continue;
        if (childSeen.has(childHref)) continue;
        childSeen.add(childHref);
        const grandchildChildren = Array.isArray((rawChild as any).children)
          ? (rawChild as any).children
          : undefined;
        const child: PlatformNavLink = { id: childId, label: childLabel, href: childHref };
        if (grandchildChildren) {
          const deepWalk = walk(rawChild, i);
          if (deepWalk?.children?.length) child.children = deepWalk.children;
        }
        children.push(child);
      }
      if (children.length > 0) normalized.children = children;
    }
    return normalized;
  };
  const links: PlatformNavLink[] = [];
  value.forEach((entry, idx) => {
    const normalized = walk(entry, idx);
    if (normalized) links.push(normalized);
  });
  return links.length > 0 ? links : fallback;
}

function normalizeFooterSections(value: unknown, fallback: PlatformFooterSection[]) {
  if (!Array.isArray(value)) return fallback;
  const sections = value
    .filter(isObject)
    .map((entry, index) => {
      const title = typeof entry.title === 'string' && entry.title.trim() ? entry.title.trim() : '';
      const normalizedTitle = normalizeLegacyPublicLabel(title);
      const stableSectionId =
        LEGACY_FOOTER_SECTION_TITLE_TO_ID[
          normalizedTitle as keyof typeof LEGACY_FOOTER_SECTION_TITLE_TO_ID
        ];
      const rawId = typeof entry.id === 'string' && entry.id.trim() ? entry.id.trim() : '';

      return {
        id: stableSectionId || rawId || `section-${index}`,
        title,
        links: normalizeNavLinks(entry.links, []),
      };
    })
    .filter((section) => section.title && section.links.length > 0);
  return sections.length > 0 ? sections : fallback;
}

export function isDefaultPublicContactAddress(value: string) {
  return normalizeLegacyPublicLabel(value) === normalizeLegacyPublicLabel(DEFAULT_PUBLIC_CONTACT_ADDRESS);
}

function hexToRgb(hex: string) {
  const safe = sanitizeHexColor(hex, '#000000').slice(1);
  return {
    r: parseInt(safe.slice(0, 2), 16),
    g: parseInt(safe.slice(2, 4), 16),
    b: parseInt(safe.slice(4, 6), 16),
  };
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${[r, g, b].map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0')).join('')}`;
}

function mixHex(base: string, target: string, weight: number) {
  const a = hexToRgb(base);
  const b = hexToRgb(target);
  return rgbToHex(
    a.r + (b.r - a.r) * weight,
    a.g + (b.g - a.g) * weight,
    a.b + (b.b - a.b) * weight
  );
}

function getContrastingForeground(hex: string) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance >= 170 ? '#0f172a' : '#ffffff';
}

export function normalizePlatformSettings(value: unknown): PlatformSettingsSnapshot {
  const raw = isObject(value) ? value : {};
  const appName = sanitizeNonEmptyString(raw.app_name, DEFAULT_PLATFORM_SETTINGS.branding.appName);
  const logoUrl = sanitizeNonEmptyString(raw.logo_url, DEFAULT_PLATFORM_SETTINGS.branding.logoUrl);
  const compactLogoUrl = sanitizeOptionalString(raw.compact_logo_url) || logoUrl || DEFAULT_PLATFORM_SETTINGS.branding.compactLogoUrl;
  const faviconUrl = sanitizeOptionalString(raw.favicon_url) || DEFAULT_PLATFORM_SETTINGS.branding.faviconUrl;
  const appleTouchIconUrl = sanitizeOptionalString(raw.apple_touch_icon_url) || compactLogoUrl || faviconUrl || DEFAULT_PLATFORM_SETTINGS.branding.appleTouchIconUrl;
  const pwaIcon192Url = sanitizeOptionalString(raw.pwa_icon_192_url) || appleTouchIconUrl || DEFAULT_PLATFORM_SETTINGS.branding.pwaIcon192Url;
  const pwaIcon512Url = sanitizeOptionalString(raw.pwa_icon_512_url) || appleTouchIconUrl || DEFAULT_PLATFORM_SETTINGS.branding.pwaIcon512Url;
  const blockedSocialFallbackSources = [
    logoUrl,
    compactLogoUrl,
    faviconUrl,
    appleTouchIconUrl,
    pwaIcon192Url,
    pwaIcon512Url,
  ];
  const socialImageUrl =
    getApprovedSocialPreviewAsset(sanitizeOptionalString(raw.social_image_url), blockedSocialFallbackSources) ||
    DEFAULT_PLATFORM_SETTINGS.branding.socialImageUrl;
  const emailLogoUrl = sanitizeOptionalString(raw.email_logo_url) || logoUrl || DEFAULT_PLATFORM_SETTINGS.branding.emailLogoUrl;
  const organizationLogoUrl = sanitizeOptionalString(raw.organization_logo_url) || logoUrl || DEFAULT_PLATFORM_SETTINGS.branding.organizationLogoUrl;

  const branding: PlatformBrandingSettings = {
    appName,
    shortBrandName: sanitizeOptionalString(raw.short_brand_name) || appName,
    tagline: sanitizeNonEmptyString(raw.tagline, DEFAULT_PLATFORM_SETTINGS.branding.tagline),
    logoUrl,
    compactLogoUrl,
    faviconUrl,
    appleTouchIconUrl,
    pwaIcon192Url,
    pwaIcon512Url,
    socialImageUrl,
    emailLogoUrl,
    organizationLogoUrl,
    primaryColor: sanitizeHexColor(raw.primary_color, DEFAULT_PLATFORM_SETTINGS.branding.primaryColor),
    accentColor: sanitizeHexColor(raw.accent_color, DEFAULT_PLATFORM_SETTINGS.branding.accentColor),
    fontFamily: sanitizeFontFamily(raw.font_family),
  };

  const siteTitle = sanitizeNonEmptyString(raw.site_title, DEFAULT_PLATFORM_SETTINGS.seo.siteTitle);
  const siteDescription = sanitizeNonEmptyString(raw.site_description, DEFAULT_PLATFORM_SETTINGS.seo.siteDescription);
  const canonicalUrl = sanitizeOptionalString(raw.canonical_url) || DEFAULT_PLATFORM_SETTINGS.seo.canonicalUrl;
  const ogTitle = sanitizeNonEmptyString(raw.og_title, siteTitle);
  const ogDescription = sanitizeNonEmptyString(raw.og_description, siteDescription);
  const ogImage =
    getApprovedSocialPreviewAsset(sanitizeOptionalString(raw.og_image), blockedSocialFallbackSources) ||
    branding.socialImageUrl;
  const twitterTitle = sanitizeNonEmptyString(raw.twitter_title, ogTitle);
  const twitterDescription = sanitizeNonEmptyString(raw.twitter_description, ogDescription);
  const twitterImage =
    getApprovedSocialPreviewAsset(sanitizeOptionalString(raw.twitter_image), blockedSocialFallbackSources) ||
    ogImage;
  const robotsIndex = sanitizeBoolean(raw.robots_index, DEFAULT_PLATFORM_SETTINGS.seo.robotsIndex);
  const robotsFollow = sanitizeBoolean(raw.robots_follow, DEFAULT_PLATFORM_SETTINGS.seo.robotsFollow);
  const homeOgTitle = sanitizeOptionalString(raw.home_og_title) || ogTitle;
  const homeOgDescription = sanitizeOptionalString(raw.home_og_description) || ogDescription;
  const homeSocialImage =
    getApprovedSocialPreviewAsset(
      sanitizeOptionalString(raw.home_social_image_url),
      blockedSocialFallbackSources
    ) || ogImage;

  const seo: PlatformSeoSettings = {
    siteTitle,
    titleTemplate: sanitizeNonEmptyString(raw.title_template, `%s | ${branding.appName}`),
    siteDescription,
    keywords: normalizeSeoKeywordList(raw.keywords, DEFAULT_PLATFORM_SETTINGS.seo.keywords),
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogImage,
    twitterTitle,
    twitterDescription,
    twitterImage,
    twitterHandle: sanitizeOptionalString(raw.twitter_handle) || DEFAULT_PLATFORM_SETTINGS.seo.twitterHandle,
    twitterCard: sanitizeTwitterCard(raw.twitter_card ?? raw.twitter_card_type),
    robotsIndex,
    robotsFollow,
    sitemapEnabled: sanitizeBoolean(raw.sitemap_enabled, DEFAULT_PLATFORM_SETTINGS.seo.sitemapEnabled),
    googleSiteVerification: sanitizeOptionalString(raw.google_site_verification),
    bingSiteVerification: sanitizeOptionalString(raw.bing_site_verification),
    organizationName: sanitizeOptionalString(raw.organization_name) || branding.appName,
    organizationLegalName: sanitizeOptionalString(raw.organization_legal_name),
    organizationDescription: sanitizeOptionalString(raw.organization_description) || siteDescription,
    home: {
      title: sanitizeOptionalString(raw.home_seo_title) || siteTitle,
      description: sanitizeOptionalString(raw.home_seo_description) || siteDescription,
      keywords: normalizeSeoKeywordList(raw.home_seo_keywords, []),
      ogTitle: homeOgTitle,
      ogDescription: homeOgDescription,
      socialImage: homeSocialImage,
      twitterTitle: sanitizeOptionalString(raw.home_twitter_title) || homeOgTitle || twitterTitle,
      twitterDescription:
        sanitizeOptionalString(raw.home_twitter_description) ||
        homeOgDescription ||
        twitterDescription,
      twitterImage:
        getApprovedSocialPreviewAsset(
          sanitizeOptionalString(raw.home_twitter_image),
          blockedSocialFallbackSources
        ) ||
        homeSocialImage ||
        twitterImage,
      robotsIndex:
        typeof raw.home_robots_index === 'boolean' ? raw.home_robots_index : robotsIndex,
      robotsFollow:
        typeof raw.home_robots_follow === 'boolean' ? raw.home_robots_follow : robotsFollow,
    },
  };

  const publicUi: PlatformPublicSettings = {
    headerMenu: normalizeNavLinks(raw.header_menu, DEFAULT_HEADER_MENU),
    stickyHeader: sanitizeBoolean(raw.sticky_header, DEFAULT_PLATFORM_SETTINGS.publicUi.stickyHeader),
    footerSections: normalizeFooterSections(raw.footer_sections, DEFAULT_FOOTER_SECTIONS),
    footerTagline: sanitizeOptionalString(raw.footer_tagline) || branding.tagline,
    footerCompanyName: branding.appName,
    footerWebsiteUrl: canonicalUrl,
    footerCopyright: sanitizeOptionalString(raw.footer_copyright) || DEFAULT_PLATFORM_SETTINGS.publicUi.footerCopyright,
    footerPoweredByText:
      sanitizeOptionalString(raw.footer_powered_by_text) || DEFAULT_PLATFORM_SETTINGS.publicUi.footerPoweredByText,
    footerPoweredByUrl:
      sanitizeOptionalString(raw.footer_powered_by_url) || DEFAULT_PLATFORM_SETTINGS.publicUi.footerPoweredByUrl,
    contactEmail: sanitizeOptionalString(raw.contact_email),
    contactPhone: sanitizeOptionalString(raw.contact_phone),
    contactPhoneCountryCode: sanitizeOptionalString(raw.contact_phone_country_code).toUpperCase(),
    contactPhoneFormatted: '',
    contactAddress: sanitizeOptionalString(raw.contact_address),
    socialTwitter: sanitizeOptionalString(raw.social_twitter),
    socialGithub: sanitizeOptionalString(raw.social_github),
    socialLinkedin: sanitizeOptionalString(raw.social_linkedin),
  };

  const email: PlatformEmailSettings = {
    provider: raw.email_provider === 'smtp' ? 'smtp' : 'supabase',
    smtpHost: sanitizeOptionalString(raw.smtp_host),
    smtpPort: sanitizeOptionalString(raw.smtp_port) || DEFAULT_PLATFORM_SETTINGS.email.smtpPort,
    smtpUser: sanitizeOptionalString(raw.smtp_user),
    fromEmail: sanitizeOptionalString(raw.from_email) || DEFAULT_PLATFORM_SETTINGS.email.fromEmail,
    fromName: sanitizeOptionalString(raw.from_name) || DEFAULT_PLATFORM_SETTINGS.email.fromName,
    replyToEmail: sanitizeOptionalString(raw.reply_to_email) || DEFAULT_PLATFORM_SETTINGS.email.replyToEmail,
    supportEmail: publicUi.contactEmail || DEFAULT_PLATFORM_SETTINGS.email.supportEmail,
    emailLogoUrl: sanitizeOptionalString(raw.email_logo_url) || branding.emailLogoUrl,
    footerCompanyName: branding.appName,
    footerWebsiteUrl: canonicalUrl,
    footerCopyright: publicUi.footerCopyright,
    testRecipientEmail: sanitizeOptionalString(raw.test_recipient_email),
  };

  const analytics: PlatformAnalyticsSettings = {
    googleAnalyticsId: sanitizeOptionalString(raw.google_analytics_id),
    googleTagManagerId: sanitizeOptionalString(raw.google_tag_manager_id),
  };

  return {
    branding,
    seo,
    publicUi,
    email,
    analytics,
    auth: {
      emailPasswordEnabled: sanitizeBoolean(
        raw.email_password_enabled,
        DEFAULT_PLATFORM_SETTINGS.auth.emailPasswordEnabled
      ),
      googleOauthEnabled: sanitizeBoolean(
        raw.google_oauth_enabled,
        DEFAULT_PLATFORM_SETTINGS.auth.googleOauthEnabled
      ),
      appleOauthEnabled: sanitizeBoolean(
        raw.apple_oauth_enabled,
        DEFAULT_PLATFORM_SETTINGS.auth.appleOauthEnabled
      ),
      magicLinkEnabled: sanitizeBoolean(
        raw.magic_link_enabled,
        DEFAULT_PLATFORM_SETTINGS.auth.magicLinkEnabled
      ),
      requireEmailVerification: sanitizeBoolean(
        raw.require_email_verification,
        DEFAULT_PLATFORM_SETTINGS.auth.requireEmailVerification
      ),
      passwordMinLength:
        typeof raw.password_min_length === 'number' && Number.isFinite(raw.password_min_length)
          ? raw.password_min_length
          : DEFAULT_PLATFORM_SETTINGS.auth.passwordMinLength,
    },
    localization: {
      defaultLanguage: sanitizeSupportedLanguage(raw.default_language),
      enabledLanguages: normalizeEnabledLanguages(raw.enabled_languages),
    },
    updatedAt: typeof raw.updated_at === 'string' ? raw.updated_at : undefined,
    raw,
  };
}

export function getSettingsAssetUrl(url: string, updatedAt?: string) {
  if (!url) return '';
  if (!updatedAt) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${encodeURIComponent(updatedAt)}`;
}

export function shouldShowBrandTextBesideLogo(logoUrl?: string) {
  const normalized = typeof logoUrl === 'string' ? logoUrl.trim() : '';
  if (!normalized) return true;

  const defaultLogo = DEFAULT_PLATFORM_SETTINGS.branding.logoUrl;
  return normalized === defaultLogo;
}

export function buildBrandingCssVariables(branding: PlatformBrandingSettings): CSSProperties {
  const primary = sanitizeHexColor(branding.primaryColor, DEFAULT_PLATFORM_SETTINGS.branding.primaryColor);
  const accent = sanitizeHexColor(branding.accentColor, DEFAULT_PLATFORM_SETTINGS.branding.accentColor);
  const primaryRgb = hexToRgb(primary);
  const accentRgb = hexToRgb(accent);
  const primaryForeground = getContrastingForeground(primary);
  const accentForeground = getContrastingForeground(accent);

  let fontValue = "var(--font-plus-jakarta), 'Plus Jakarta Sans', sans-serif";
  if (branding.fontFamily === 'Inter') fontValue = "var(--font-inter), 'Inter', sans-serif";
  if (branding.fontFamily === 'Poppins') fontValue = "var(--font-poppins), 'Poppins', sans-serif";
  if (branding.fontFamily === 'Roboto') fontValue = "var(--font-roboto), 'Roboto', sans-serif";

  const cssVariables: Record<string, string> = {
    '--primary': primary,
    '--primary-rgb': `${primaryRgb.r} ${primaryRgb.g} ${primaryRgb.b}`,
    '--primary-foreground': primaryForeground,
    '--accent': accent,
    '--accent-rgb': `${accentRgb.r} ${accentRgb.g} ${accentRgb.b}`,
    '--accent-foreground': accentForeground,
    '--ring': accent,
    '--navy-600': mixHex(primary, '#ffffff', 0.1),
    '--navy-700': primary,
    '--navy-800': mixHex(primary, '#000000', 0.16),
    '--teal-400': mixHex(accent, '#ffffff', 0.16),
    '--teal-500': accent,
    '--teal-600': mixHex(accent, '#000000', 0.12),
    '--font-sans': fontValue,
  };

  return cssVariables as CSSProperties;
}
